<template>
  <div class="manual-stock-check-detail">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">盘点详情</h1>
      <el-button
        @click="
          showAddModal = true;
          fetchSuppliers(1);
        "
        :disabled="inventoryCheckStatus === 1 || isSubmitting"
        type="primary"
        size="small"
      >
        新增
      </el-button>
    </header>

    <div class="main-content">
      <div class="status-container">
        <div class="search-container">
          <el-input
            v-model="searchQuery"
            placeholder="请输入物料编码进行搜索"
            @input="handleSearch"
            @keydown.enter="handleSearch"
            clearable
          />
        </div>

        <div class="progress-container">
          <div class="progress-label">
            <span>盘点进度</span>
            <span class="progress-count">
              {{ inventoriedCount }} / {{ totalCount }} 项 ({{
                inventoriedPercentage
              }}%)
            </span>
          </div>
          <el-progress
            :percentage="inventoriedPercentage"
            :stroke-width="8"
            :show-text="false"
          />
        </div>

        <div class="filter-nav">
          <el-button
            :type="activeFilter.includes(0) ? 'primary' : ''"
            @click="toggleFilter(0)"
            size="small"
          >
            全部
          </el-button>
          <el-button
            :type="activeFilter.includes(1) ? 'primary' : ''"
            @click="toggleFilter(1)"
            size="small"
          >
            良品
          </el-button>
          <el-button
            :type="activeFilter.includes(2) ? 'primary' : ''"
            @click="toggleFilter(2)"
            size="small"
          >
            不良品
          </el-button>
          <el-button
            :type="activeFilter.includes(3) ? 'primary' : ''"
            @click="toggleFilter(3)"
            size="small"
          >
            已盘
          </el-button>
          <el-button
            :type="activeFilter.includes(4) ? 'primary' : ''"
            @click="toggleFilter(4)"
            size="small"
          >
            未盘
          </el-button>
        </div>
      </div>

      <div v-loading="loading" class="inventory-cards-wrapper">
        <el-alert
          v-if="errorMsg"
          :title="errorMsg"
          type="error"
          show-icon
          :closable="false"
          class="status-alert"
        />

        <el-empty
          v-else-if="inventoryData.length === 0 && !loading"
          description="暂无盘点数据"
        />

        <div v-else class="inventory-cards">
          <div
            v-for="(item, index) in inventoryData"
            :key="item.id"
            class="inventory-card"
            :class="{ highlighted: item.highlight }"
          >
            <div class="card-header">
              <el-tag
                :type="item.isInvente === 1 ? 'success' : 'warning'"
                size="small"
                effect="dark"
                round
              >
                {{ item.isInvente === 1 ? "已盘" : "未盘" }}
              </el-tag>
              <span class="supplier-name">{{
                item.supplierName || "供应商名称"
              }}</span>
              <span class="stock-info-inline"
                >库存: {{ formatToTwoDecimals(item.stockNum) }}</span
              >
              <el-tag
                :type="item.isBad === 0 ? 'info' : 'danger'"
                size="small"
                effect="plain"
                round
              >
                {{ item.isBad === 0 ? "良品" : "不良品" }}
              </el-tag>
            </div>

            <div class="card-content">
              <div class="material-info">
                <div class="material-code">物料编码: {{ item.materialNo }}</div>
                <div class="material-desc">
                  物料描述: {{ item.materialDesc || "-" }}
                </div>
                <div class="material-ware">
                  库位: {{ item.palletCode || "-" }}
                </div>
              </div>

              <div class="quantity-section">
                <div v-if="editingIndex === index" class="quantity-edit">
                  <el-input
                    v-model="editQuantityRaw"
                    inputmode="decimal"
                    placeholder="请输入数量(支持 箱*包装数量+零头箱)"
                    @keydown="handleEditKeydown"
                    @blur="applyEditQuantity"
                  />
                  <el-button
                    class="calc-btn"
                    @click="openCalculator"
                    size="small"
                  >
                    计算器
                  </el-button>
                  <div class="edit-actions">
                    <el-button @click="cancelEdit" style="flex: 1">
                      取消
                    </el-button>
                    <el-button
                      type="primary"
                      @click="handleConfirmClick(index, item)"
                      style="flex: 1"
                    >
                      确定
                    </el-button>
                  </div>
                </div>
                <div v-else class="quantity-display">
                  <div class="qty-left">
                    <el-button
                      type="primary"
                      @click="startEdit(index, item)"
                      :disabled="inventoryCheckStatus === 1 || isSubmitting"
                      size="small"
                    >
                      {{ item.isInvente === 1 ? "修改" : "盘点" }}
                    </el-button>
                    <el-button
                      type="info"
                      plain
                      @click="handleQueryLocation(item)"
                      size="small"
                    >
                      查库位
                    </el-button>
                    <el-button
                      type="info"
                      plain
                      @click="handleQueryImage(item)"
                      size="small"
                    >
                      物料图片
                    </el-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="item.isInvente === 1" class="diff-remark-container">
              <div
                v-if="calculateDifference(item) !== 0"
                class="remark-display"
              >
                <span class="remark-label">备注：</span>
                <span class="remark-text">{{ item.remark || "未填写" }}</span>
              </div>
              <div class="qty-diff-group">
                <span class="actual-quantity">
                  已盘数量: {{ formatToTwoDecimals(item.actualNum || 0) }}
                </span>
                <div
                  class="difference-section"
                  :class="{
                    'difference-positive': calculateDifference(item) > 0,
                    'difference-negative': calculateDifference(item) < 0,
                  }"
                >
                  差异数量: {{ formatToTwoDecimals(calculateDifference(item)) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handlePageChange"
          size="small"
        />
      </div>

      <div class="audit-actions">
        <el-button
          @click="showAuditRejectConfirm"
          :disabled="
            inventoryCheckStatus === 1 ||
            isSubmitting ||
            inventoryData.length === 0
          "
        >
          审核不通过
        </el-button>
        <el-button
          type="primary"
          @click="showAuditPassConfirm"
          :disabled="
            inventoryCheckStatus === 1 ||
            isSubmitting ||
            inventoryData.length === 0
          "
        >
          审核通过
        </el-button>
      </div>
    </div>

    <!-- 错误提示弹窗 -->
    <el-dialog
      v-model="showErrorModal"
      :title="errorModalTitle"
      width="85%"
      :close-on-click-modal="false"
    >
      <p>{{ errorModalDetail }}</p>
      <p v-if="errorModalAdvice" class="advice-text">
        {{ errorModalAdvice }}
      </p>
      <template #footer>
        <el-button type="primary" @click="showErrorModal = false">
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 新增物料弹窗 -->
    <el-dialog
      v-model="showAddModal"
      title="新增物料"
      width="95%"
      :close-on-click-modal="false"
      top="5vh"
    >
      <el-form-item label="选择供应商" class="form-item">
        <el-select
          v-model="selectedSupplier"
          filterable
          remote
          reserve-keyword
          placeholder="输入供应商名称搜索"
          :remote-method="handleSupplierSearch"
          :loading="supplierLoading"
          value-key="id"
          @change="selectSupplier"
        >
          <el-option
            v-for="item in suppliers"
            :key="item.id"
            :label="item.supplierName"
            :value="item.id"
          >
            <span>{{ item.supplierName }}</span>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item v-if="selectedSupplier" label="选择物料" class="form-item">
        <el-select
          v-model="selectedMaterial"
          filterable
          remote
          reserve-keyword
          placeholder="输入物料编码搜索"
          :remote-method="handleMaterialSearch"
          :loading="materialLoading"
          value-key="id"
          @change="selectMaterial"
        >
          <el-option
            v-for="item in materials"
            :key="item.id"
            :label="item.materialNo"
            :value="item"
          >
            <span>{{ item.materialNo }}</span>
            <span style="float: right; color: #8492a6; font-size: 15px">{{
              item.materialDesc || item.materialName || ""
            }}</span>
          </el-option>
        </el-select>
      </el-form-item>

      <template #footer>
        <el-button
          @click="
            showAddModal = false;
            resetAddModal();
          "
          >取消</el-button
        >
        <el-button
          type="primary"
          @click="showConfirmModal = true"
          :disabled="isAdding || !selectedMaterial"
        >
          {{ isAdding ? "添加中..." : "添加物料" }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 确认添加弹窗 -->
    <el-dialog
      v-model="showConfirmModal"
      title="确认添加"
      width="85%"
      :close-on-click-modal="false"
    >
      <p>确定要添加以下物料到盘点列表吗？</p>
      <div class="confirm-material-info" v-if="selectedMaterial">
        <div>物料编码：{{ selectedMaterial.materialNo }}</div>
        <div>
          物料名称：{{
            selectedMaterial.materialDesc ||
            selectedMaterial.materialName ||
            "未知物料"
          }}
        </div>
      </div>
      <template #footer>
        <el-button @click="showConfirmModal = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmAddMaterial"
          :disabled="isAdding"
        >
          {{ isAdding ? "添加中..." : "确认添加" }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 审核通过确认弹窗 -->
    <el-dialog
      v-model="showAuditPassConfirmModal"
      title="确认审核通过"
      width="85%"
      :close-on-click-modal="false"
    >
      <p>确定要将此盘点单标记为审核通过吗？</p>
      <template #footer>
        <el-button @click="showAuditPassConfirmModal = false">取消</el-button>
        <el-button type="primary" @click="handleAuditPass">确认</el-button>
      </template>
    </el-dialog>

    <!-- 审核不通过确认弹窗 -->
    <el-dialog
      v-model="showAuditRejectConfirmModal"
      title="审核不通过"
      width="85%"
      :close-on-click-modal="false"
    >
      <el-form-item label="不通过原因">
        <el-input
          v-model="reason"
          type="textarea"
          placeholder="请输入审核不通过的原因"
          :rows="4"
        />
      </el-form-item>
      <template #footer>
        <el-button
          @click="
            showAuditRejectConfirmModal = false;
            reason = '';
          "
        >
          取消
        </el-button>
        <el-button
          type="primary"
          @click="handleAuditReject"
          :disabled="!reason.trim() || isSubmitting"
        >
          {{ isSubmitting ? "提交中..." : "确认不通过" }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 计算器弹窗 -->
    <el-dialog
      v-model="showCalculatorModal"
      title="计算器"
      width="90%"
      :close-on-click-modal="false"
    >
      <div class="calc-display">
        <el-input
          v-model="calcExpression"
          type="text"
          inputmode="decimal"
          placeholder="输入表达式"
        />
        <div v-if="calcError" class="calc-error">{{ calcError }}</div>
      </div>
      <div class="calc-grid">
        <el-button class="calc-key" @click="calcAppend('7')">7</el-button>
        <el-button class="calc-key" @click="calcAppend('8')">8</el-button>
        <el-button class="calc-key" @click="calcAppend('9')">9</el-button>
        <el-button class="calc-key op" @click="calcAppend('/')">/</el-button>

        <el-button class="calc-key" @click="calcAppend('4')">4</el-button>
        <el-button class="calc-key" @click="calcAppend('5')">5</el-button>
        <el-button class="calc-key" @click="calcAppend('6')">6</el-button>
        <el-button class="calc-key op" @click="calcAppend('*')">*</el-button>

        <el-button class="calc-key" @click="calcAppend('1')">1</el-button>
        <el-button class="calc-key" @click="calcAppend('2')">2</el-button>
        <el-button class="calc-key" @click="calcAppend('3')">3</el-button>
        <el-button class="calc-key op" @click="calcAppend('-')">-</el-button>

        <el-button class="calc-key" @click="calcAppend('0')">0</el-button>
        <el-button class="calc-key" @click="calcAppend('.')">.</el-button>
        <el-button class="calc-key" @click="calcBackspace">⌫</el-button>
        <el-button class="calc-key op" @click="calcAppend('+')">+</el-button>

        <el-button class="calc-key wide" @click="calcClear">清空</el-button>
        <el-button class="calc-key wide primary" @click="calcEvaluate">
          计算并回填
        </el-button>
      </div>
    </el-dialog>

    <!-- 查库位弹窗 -->
    <el-dialog
      v-model="showLocationModal"
      :title="`库位信息 - ${locationMaterialNo}`"
      width="80%"
      :close-on-click-modal="false"
    >
      <div v-if="locationLoading" v-loading="true" style="min-height: 100px" />
      <el-empty
        v-else-if="locationList.length === 0"
        description="暂无库位信息"
        :image-size="60"
      />
      <el-table v-else :data="locationList" stripe size="small">
        <el-table-column prop="wareName" label="库位" min-width="100" />
        <el-table-column
          prop="stockNum"
          label="库存数量"
          width="100"
          align="center"
        />
        <el-table-column prop="isBad" label="类型" width="80" align="center">
          <template #default="{ row }">
            <el-tag
              :type="row.isBad === 1 ? 'danger' : 'success'"
              size="small"
              round
            >
              {{ row.isBad === 1 ? "不良品" : "良品" }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="showLocationModal = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 物料图片弹窗 -->
    <el-dialog
      v-model="showImageDialog"
      :title="`物料图片 - ${imageMaterialNo}`"
      width="80%"
      :close-on-click-modal="true"
    >
      <div v-if="imageLoading" v-loading="true" style="min-height: 100px" />
      <template v-else>
        <div
          v-if="formatImageUrl(materialImageUrl) && !imageLoadError"
          class="image-preview-area"
        >
          <img
            :src="formatImageUrl(materialImageUrl)"
            class="material-image"
            @click="openImagePreview(formatImageUrl(materialImageUrl))"
            @error="handleImageError"
          />
          <div class="image-zoom-hint">
            <el-icon><ZoomIn /></el-icon>
            <span>点击查看大图</span>
          </div>
        </div>
        <el-empty v-else description="暂无物料图片" :image-size="80" />
      </template>
      <template #footer>
        <el-button @click="showImageDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 图片大图预览 -->
    <teleport to="body">
      <div
        v-if="showImagePreview"
        class="image-preview-overlay"
        @click.self="closeImagePreview"
      >
        <div
          class="image-preview-container"
          @dblclick="toggleZoom"
          @wheel.prevent="handleWheel"
          @touchstart.passive="handleTouchStart"
          @touchmove.passive="handleTouchMove"
          @touchend="handleTouchEnd"
          @mousedown="handleMouseDown"
        >
          <img
            :src="previewImageUrl"
            class="preview-image"
            :style="previewImageStyle"
            @dragstart.prevent
          />
        </div>
        <div class="preview-toolbar">
          <el-button circle size="small" @click="zoomOut">
            <el-icon><ZoomOut /></el-icon>
          </el-button>
          <span class="zoom-level">{{ Math.round(previewScale * 100) }}%</span>
          <el-button circle size="small" @click="zoomIn">
            <el-icon><ZoomIn /></el-icon>
          </el-button>
          <el-button circle size="small" @click="resetZoom">
            <el-icon><RefreshLeft /></el-icon>
          </el-button>
          <el-button circle size="small" @click="closeImagePreview">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>
        <div class="preview-hint">双击缩放 · 双指缩放 · 拖拽移动</div>
      </div>
    </teleport>

    <!-- 盘点备注弹窗（库位盘点模式：无论是否有差异均需填写） -->
    <el-dialog
      v-model="showRemarkModal"
      title="填写盘点备注"
      width="90%"
      :close-on-click-modal="false"
    >
      <div class="modal-info">
        <div class="info-item">
          <span class="info-label">物料编码：</span>
          <span class="info-value">{{
            currentEditItem?.item.materialNo || ""
          }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">库存数量：</span>
          <span class="info-value">{{
            formatToTwoDecimals(currentEditItem?.inventory || 0)
          }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">盘点数量：</span>
          <span class="info-value">{{
            formatToTwoDecimals(currentEditItem?.actual || 0)
          }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">差异数量：</span>
          <span
            class="info-value"
            :class="{
              'difference-positive': currentEditItem?.difference > 0,
              'difference-negative': currentEditItem?.difference < 0,
            }"
          >
            {{ formatToTwoDecimals(currentEditItem?.difference || 0) }}
          </span>
        </div>
      </div>
      <el-form-item label="备注" class="form-item">
        <el-input
          v-model="editRemarkRaw"
          type="textarea"
          placeholder="请填写盘点备注（选填）"
          :rows="3"
          autofocus
        />
      </el-form-item>
      <template #footer>
        <el-button @click="cancelRemark">取消</el-button>
        <el-button type="primary" @click="confirmRemark">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, nextTick, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ElMessage } from "element-plus";
import { ZoomIn, ZoomOut, Close, RefreshLeft } from "@element-plus/icons-vue";
import {
  getSuppliersByGroupId,
  getMaterialsBySupplierId,
} from "@/api/projectTeamStrockCheck";
import {
  addStockCheckDetail,
  getStockCheckDetailPage,
  updateStockCheckStatus,
  updateStockCheckDetail,
  getMaterialStockPage,
} from "@/api/stockCheck";
import service from "@/utils/request";

export default {
  name: "LocationStockCheckDetail",
  components: { ZoomIn, ZoomOut, Close, RefreshLeft },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const inventoryId = route.params.id;

    const statusMsg = ref("");
    const statusType = ref("success");
    const inventoryData = ref([]);
    const loading = ref(false);
    const errorMsg = ref("");
    const activeFilter = ref([0]);
    const editingIndex = ref(-1);
    const editQuantityRaw = ref("");
    const editRemarkRaw = ref("");
    const isSubmitting = ref(false);
    const showRemarkModal = ref(false);
    const currentEditItem = ref(null);

    const editingItemMeta = ref(null);

    const showCalculatorModal = ref(false);
    const calcExpression = ref("");
    const calcError = ref("");

    const showLocationModal = ref(false);
    const locationLoading = ref(false);
    const locationList = ref([]);
    const locationMaterialNo = ref("");

    // 物料图片查看
    const showImageDialog = ref(false);
    const imageLoading = ref(false);
    const materialImageUrl = ref("");
    const imageMaterialNo = ref("");

    const showImagePreview = ref(false);
    const previewImageUrl = ref("");
    const previewScale = ref(1);
    const previewX = ref(0);
    const previewY = ref(0);
    const isImgDragging = ref(false);
    const imgDragStart = ref({ x: 0, y: 0, tx: 0, ty: 0 });
    const touchStartDist = ref(0);
    const touchStartScale = ref(1);

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 5;

    const previewImageStyle = computed(() => ({
      transform: `translate(${previewX.value}px, ${previewY.value}px) scale(${previewScale.value})`,
      transition: isImgDragging.value ? "none" : "transform 0.2s ease",
    }));

    const reason = ref("");
    const showAuditPassConfirmModal = ref(false);
    const showAuditRejectConfirmModal = ref(false);

    const inventoryCheckStatus = ref(0);

    const bizDept = ref("");

    const searchQuery = ref("");

    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);

    const showErrorModal = ref(false);
    const errorModalTitle = ref("提示");
    const errorModalDetail = ref("");
    const errorModalAdvice = ref("");

    const showAddModal = ref(false);
    const showConfirmModal = ref(false);
    const suppliers = ref([]);
    const materials = ref([]);
    const selectedSupplier = ref("");
    const selectedMaterial = ref(null);
    const supplierSearchKeyword = ref("");
    const materialSearchKeyword = ref("");
    const supplierLoading = ref(false);
    const materialLoading = ref(false);
    const supplierCurrentPage = ref(1);
    const materialCurrentPage = ref(1);
    const supplierPageSize = ref(20);
    const materialPageSize = ref(20);
    const supplierTotal = ref(0);
    const materialTotal = ref(0);
    const isAdding = ref(false);

    const totalCount = computed(() => inventoryData.value.length);
    const inventoriedCount = computed(() => {
      return inventoryData.value.filter((item) => item.isInvente === 1).length;
    });
    const inventoriedPercentage = computed(() => {
      if (totalCount.value === 0) return 0;
      return Math.round((inventoriedCount.value / totalCount.value) * 100);
    });

    const fetchBizDept = async () => {
      try {
        bizDept.value = route.query.bizDept || "";
      } catch (error) {
        console.error("获取项目组ID失败:", error);
        setStatus("项目组信息获取失败，请刷新重试", "error");
      }
    };

    const fetchSuppliers = async (page = 1) => {
      if (!bizDept.value) {
        setStatus("未获取到项目组信息，无法加载供应商", "error");
        return;
      }

      try {
        supplierLoading.value = true;
        const response = await getSuppliersByGroupId({
          current: page,
          size: supplierPageSize.value,
          bizDept: bizDept.value,
          supplierName: supplierSearchKeyword.value,
          supplierCode: supplierSearchKeyword.value,
        });

        suppliers.value = response.data?.records || [];
        supplierTotal.value = response.data?.total || 0;
        supplierCurrentPage.value = page;
      } catch (error) {
        console.error("获取供应商失败:", error);
        setStatus("获取供应商失败，请重试", "error");
      } finally {
        supplierLoading.value = false;
      }
    };

    const fetchMaterials = async (page = 1) => {
      if (!selectedSupplier.value) return;

      try {
        materialLoading.value = true;
        const response = await getMaterialsBySupplierId({
          current: page,
          size: materialPageSize.value,
          supplierId: selectedSupplier.value,
          materialNo: materialSearchKeyword.value,
        });

        materials.value = response.data?.records || [];
        materialTotal.value = response.data?.total || 0;
        materialCurrentPage.value = page;
      } catch (error) {
        console.error("获取物料失败:", error);
        setStatus("获取物料失败，请重试", "error");
      } finally {
        materialLoading.value = false;
      }
    };

    const handleSupplierSearch = (query) => {
      supplierSearchKeyword.value = query || "";
      fetchSuppliers(1);
    };

    const handleMaterialSearch = (query) => {
      materialSearchKeyword.value = query || "";
      fetchMaterials(1);
    };

    const selectSupplier = (supplierId) => {
      selectedSupplier.value = supplierId;
      selectedMaterial.value = null;
      materialSearchKeyword.value = "";
      materials.value = [];
      if (supplierId) {
        fetchMaterials();
      }
    };

    const selectMaterial = (material) => {
      selectedMaterial.value = material;
    };

    const confirmAddMaterial = async () => {
      if (!selectedMaterial.value) return;
      try {
        isAdding.value = true;
        const response = await addStockCheckDetail({
          inventeId: inventoryId,
          supplierId: selectedSupplier.value,
          materialNo: selectedMaterial.value.materialNo,
        });

        if (response.success) {
          showConfirmModal.value = false;
          showAddModal.value = false;
          ElMessage.success(
            `物料 ${selectedMaterial.value.materialNo} 添加成功`,
          );
          fetchInventoryDetails();
        } else {
          ElMessage.error(response.msg || "添加失败");
        }
      } catch (error) {
        console.error("添加物料失败:", error);
        ElMessage.error(
          error.response?.data?.msg || error.message || "网络异常，请稍后重试",
        );
      } finally {
        isAdding.value = false;
      }
    };

    const resetAddModal = () => {
      selectedSupplier.value = "";
      selectedMaterial.value = null;
      supplierSearchKeyword.value = "";
      materialSearchKeyword.value = "";
      materials.value = [];
      supplierCurrentPage.value = 1;
      materialCurrentPage.value = 1;
    };

    const formatToTwoDecimals = (value) => {
      if (value === null || value === undefined) return "0.00";
      return parseFloat(value).toFixed(2);
    };

    const calculateDifference = (item) => {
      const actual = parseFloat(item.actualNum || 0);
      const inventory = parseFloat(item.stockNum || 0);
      return actual - inventory;
    };

    const setStatus = (message, type = "success") => {
      statusMsg.value = message;
      statusType.value = type;
      setTimeout(() => {
        if (statusMsg.value === message) {
          statusMsg.value = "";
          statusType.value = "success";
        }
      }, 3000);
    };

    const toggleFilter = async (filterType) => {
      let newFilters = [...activeFilter.value];

      if (filterType === 0) {
        if (newFilters.includes(0)) {
          newFilters = [];
        } else {
          newFilters = [0];
        }
      } else {
        if (newFilters.includes(0)) {
          newFilters = newFilters.filter((type) => type !== 0);
        }

        if (filterType === 3 || filterType === 4) {
          const otherType = filterType === 3 ? 4 : 3;
          newFilters = newFilters.filter((type) => type !== otherType);

          if (newFilters.includes(filterType)) {
            newFilters = newFilters.filter((type) => type !== filterType);
          } else {
            newFilters.push(filterType);
          }
        } else if (filterType === 1 || filterType === 2) {
          if (newFilters.includes(filterType)) {
            newFilters = newFilters.filter((type) => type !== filterType);
          } else {
            newFilters.push(filterType);
          }

          if (newFilters.includes(1) && newFilters.includes(2)) {
            newFilters = newFilters.filter((type) => type !== 1 && type !== 2);
          }
        }
      }

      activeFilter.value = newFilters;
      await fetchInventoryDetails(1);
    };

    const handleSearch = async () => {
      await fetchInventoryDetails(1);
    };

    const handlePageChange = async (page) => {
      await fetchInventoryDetails(page);
    };

    const startEdit = async (index, item) => {
      editingIndex.value = index;
      editingItemMeta.value = item
        ? { materialId: item.materialId, isBad: item.isBad || 0 }
        : null;

      if (
        item?.isInvente === 1 &&
        item.actualNum !== undefined &&
        item.actualNum !== null
      ) {
        editQuantityRaw.value = String(item.actualNum);
        editRemarkRaw.value = String(item.remark || "");
      } else {
        editQuantityRaw.value = "";
        editRemarkRaw.value = "";
      }
      await nextTick();
      document.querySelector(".quantity-input")?.focus();
    };

    const saveFormulaIfPossible = (expression) => {
      const meta = editingItemMeta.value;
      if (!meta?.materialId) return;
      const key = `manual_stock_check_formula:${inventoryId}:${
        meta.materialId
      }:${meta.isBad || 0}`;
      const text = String(expression ?? "").trim();
      if (!text) return;

      const existing = localStorage.getItem(key) || "";
      const isPureNumber = /^\d+(?:\.\d+)?$/.test(text);
      if (isPureNumber && existing && existing !== text) {
        return;
      }
      localStorage.setItem(key, text);
    };

    const evaluateQuantityExpression = (raw) => {
      const text = String(raw ?? "").trim();
      if (!text) return { ok: false, error: "请输入盘点数量" };

      const cleaned = text.replace(/\s+/g, "");
      if (!/^[0-9+\-*/().]+$/.test(cleaned)) {
        return { ok: false, error: "仅支持数字和 + - * / ( ) 计算" };
      }

      const tokens = [];
      let i = 0;
      while (i < cleaned.length) {
        const ch = cleaned[i];
        if ((ch >= "0" && ch <= "9") || ch === ".") {
          let j = i + 1;
          while (j < cleaned.length) {
            const c = cleaned[j];
            if ((c >= "0" && c <= "9") || c === ".") j++;
            else break;
          }
          const numStr = cleaned.slice(i, j);
          const num = Number(numStr);
          if (!Number.isFinite(num))
            return { ok: false, error: "数字格式不正确" };
          tokens.push({ type: "num", value: num });
          i = j;
          continue;
        }

        if ("+-*/()".includes(ch)) {
          tokens.push({ type: "op", value: ch });
          i++;
          continue;
        }

        return { ok: false, error: "表达式包含非法字符" };
      }

      const output = [];
      const ops = [];
      const prec = { "+": 1, "-": 1, "*": 2, "/": 2, "u-": 3 };
      const assocRight = { "u-": true };
      let prev = null;

      const pushOp = (op) => {
        while (ops.length) {
          const top = ops[ops.length - 1];
          if (top === "(") break;
          const pTop = prec[top];
          const pOp = prec[op];
          const right = !!assocRight[op];
          if (pTop > pOp || (pTop === pOp && !right)) output.push(ops.pop());
          else break;
        }
        ops.push(op);
      };

      for (const t of tokens) {
        if (t.type === "num") {
          output.push(t.value);
          prev = "num";
          continue;
        }

        const op = t.value;
        if (op === "(") {
          ops.push("(");
          prev = "(";
          continue;
        }
        if (op === ")") {
          while (ops.length && ops[ops.length - 1] !== "(")
            output.push(ops.pop());
          if (!ops.length) return { ok: false, error: "括号不匹配" };
          ops.pop();
          prev = ")";
          continue;
        }

        if (op === "-" && (prev === null || prev === "op" || prev === "(")) {
          pushOp("u-");
        } else {
          pushOp(op);
        }
        prev = "op";
      }

      while (ops.length) {
        const op = ops.pop();
        if (op === "(") return { ok: false, error: "括号不匹配" };
        output.push(op);
      }

      const stack = [];
      for (const x of output) {
        if (typeof x === "number") {
          stack.push(x);
          continue;
        }
        if (x === "u-") {
          if (stack.length < 1) return { ok: false, error: "表达式不完整" };
          const a = stack.pop();
          stack.push(-a);
          continue;
        }
        if (stack.length < 2) return { ok: false, error: "表达式不完整" };
        const b = stack.pop();
        const a = stack.pop();
        let r = 0;
        if (x === "+") r = a + b;
        else if (x === "-") r = a - b;
        else if (x === "*") r = a * b;
        else if (x === "/") {
          if (b === 0) return { ok: false, error: "除数不能为0" };
          r = a / b;
        }
        if (!Number.isFinite(r)) return { ok: false, error: "计算结果无效" };
        stack.push(r);
      }

      if (stack.length !== 1) return { ok: false, error: "表达式不完整" };
      return { ok: true, value: stack[0] };
    };

    const applyEditQuantity = () => {
      const rawExpression = String(editQuantityRaw.value ?? "").trim();
      saveFormulaIfPossible(rawExpression);

      const evaluated = evaluateQuantityExpression(editQuantityRaw.value);
      if (!evaluated.ok) {
        setStatus(evaluated.error || "请输入有效的盘点数量", "error");
        return { ok: false };
      }

      if (isNaN(evaluated.value) || evaluated.value < 0) {
        setStatus("请输入有效的盘点数量（大于等于0）", "error");
        return { ok: false };
      }

      const formatted = formatToTwoDecimals(evaluated.value);
      editQuantityRaw.value = formatted;
      return { ok: true, value: evaluated.value, formatted };
    };

    const openCalculator = () => {
      calcError.value = "";
      const meta = editingItemMeta.value;
      const key = meta?.materialId
        ? `manual_stock_check_formula:${inventoryId}:${meta.materialId}:${
            meta.isBad || 0
          }`
        : "";
      const saved = key ? localStorage.getItem(key) || "" : "";
      calcExpression.value = (
        saved || String(editQuantityRaw.value || "")
      ).trim();
      showCalculatorModal.value = true;
    };

    const closeCalculator = () => {
      showCalculatorModal.value = false;
      calcError.value = "";
    };

    const calcAppend = (val) => {
      calcError.value = "";
      calcExpression.value = `${calcExpression.value}${val}`;
    };

    const calcBackspace = () => {
      calcError.value = "";
      calcExpression.value = calcExpression.value.slice(0, -1);
    };

    const calcClear = () => {
      calcError.value = "";
      calcExpression.value = "";
    };

    const calcEvaluate = () => {
      saveFormulaIfPossible(calcExpression.value);
      const evaluated = evaluateQuantityExpression(calcExpression.value);
      if (!evaluated.ok) {
        calcError.value = evaluated.error || "表达式不正确";
        return;
      }
      if (isNaN(evaluated.value) || evaluated.value < 0) {
        calcError.value = "请输入有效的盘点数量（大于等于0）";
        return;
      }
      const formatted = formatToTwoDecimals(evaluated.value);
      editQuantityRaw.value = formatted;
      calcExpression.value = formatted;
      showCalculatorModal.value = false;
    };

    const handleQueryLocation = async (item) => {
      showLocationModal.value = true;
      locationLoading.value = true;
      locationList.value = [];
      locationMaterialNo.value = item.materialNo || "";

      try {
        const res = await getMaterialStockPage({
          materialId: item.materialId || item.id,
          materialNo: item.materialNo || "",
          current: 1,
          size: 20,
        });
        if (res.success && res.data) {
          const records = res.data.records || [];
          const locations = [];
          records.forEach((record) => {
            if (Array.isArray(record.materialStockDetailList)) {
              record.materialStockDetailList.forEach((detail) => {
                locations.push({
                  wareName: detail.wareName || "-",
                  stockNum: detail.stockNum || "0",
                  isBad: detail.isBad || 0,
                });
              });
            }
          });
          locationList.value = locations;
        }
      } catch (err) {
        console.error("查询库位失败", err);
      } finally {
        locationLoading.value = false;
      }
    };

    // ===== 物料图片查看 =====
    const formatImageUrl = (url) => {
      if (!url) return "";
      let cleanUrl = url.trim().replace(/`/g, "");
      // 自动修复缺失obs的错误域名
      cleanUrl = cleanUrl.replace(
        "wms-zwl.cn-south-1.myhuaweicloud.com",
        "wms-zwl.obs.cn-south-1.myhuaweicloud.com",
      );
      return cleanUrl;
    };

    const imageLoadError = ref(false);

    const handleImageError = () => {
      imageLoadError.value = true;
      console.error("图片加载失败:", formatImageUrl(materialImageUrl.value));
    };

    const handleQueryImage = async (item) => {
      const materialId = item.materialId || item.id;
      if (!materialId) {
        ElMessage.error("缺少物料ID");
        return;
      }

      showImageDialog.value = true;
      imageLoading.value = true;
      materialImageUrl.value = "";
      imageMaterialNo.value = item.materialNo || "";
      imageLoadError.value = false;

      try {
        const res = await service.get("/uic-system/material/detail", {
          params: { id: materialId },
        });
        console.log("物料详情返回:", res);
        if (res?.success && res?.data) {
          const imgUrl = res.data.image || "";
          console.log("原始image字段:", imgUrl);
          materialImageUrl.value = imgUrl;
          // 验证图片URL格式
          if (imgUrl) {
            const cleanedUrl = formatImageUrl(imgUrl);
            console.log("清理后图片URL:", cleanedUrl);
          }
        }
      } catch (err) {
        console.error("查询物料图片失败", err);
        ElMessage.error("查询物料图片失败");
      } finally {
        imageLoading.value = false;
      }
    };

    const openImagePreview = (url) => {
      if (!url) {
        ElMessage.info("暂无图片");
        return;
      }
      previewImageUrl.value = url;
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
      showImagePreview.value = true;
    };

    const closeImagePreview = () => {
      showImagePreview.value = false;
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
    };

    const zoomIn = () => {
      previewScale.value = Math.min(MAX_SCALE, previewScale.value + 0.25);
    };

    const zoomOut = () => {
      previewScale.value = Math.max(MIN_SCALE, previewScale.value - 0.25);
    };

    const resetZoom = () => {
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
    };

    const toggleZoom = () => {
      if (previewScale.value > 1) {
        resetZoom();
      } else {
        previewScale.value = 2.5;
      }
    };

    const handleWheel = (e) => {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      previewScale.value = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, previewScale.value + delta),
      );
    };

    const handleMouseDown = (e) => {
      if (previewScale.value <= 1) return;
      isImgDragging.value = true;
      imgDragStart.value = {
        x: e.clientX,
        y: e.clientY,
        tx: previewX.value,
        ty: previewY.value,
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
      if (!isImgDragging.value) return;
      previewX.value =
        imgDragStart.value.tx + (e.clientX - imgDragStart.value.x);
      previewY.value =
        imgDragStart.value.ty + (e.clientY - imgDragStart.value.y);
    };

    const handleMouseUp = () => {
      isImgDragging.value = false;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchStartDist.value = Math.sqrt(dx * dx + dy * dy);
        touchStartScale.value = previewScale.value;
      } else if (e.touches.length === 1 && previewScale.value > 1) {
        isImgDragging.value = true;
        imgDragStart.value = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          tx: previewX.value,
          ty: previewY.value,
        };
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && touchStartDist.value > 0) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        previewScale.value = Math.min(
          MAX_SCALE,
          Math.max(
            MIN_SCALE,
            touchStartScale.value * (dist / touchStartDist.value),
          ),
        );
      } else if (e.touches.length === 1 && isImgDragging.value) {
        previewX.value =
          imgDragStart.value.tx + (e.touches[0].clientX - imgDragStart.value.x);
        previewY.value =
          imgDragStart.value.ty + (e.touches[0].clientY - imgDragStart.value.y);
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        touchStartDist.value = 0;
        isImgDragging.value = false;
      }
    };

    const confirmEdit = async (index, item) => {
      if (isSubmitting.value) return;

      try {
        const applied = applyEditQuantity();
        if (!applied.ok) return;

        const formattedQuantity = editQuantityRaw.value;

        const actual = parseFloat(formattedQuantity);
        const inventory = parseFloat(item.stockNum || 0);
        const difference = actual - inventory;

        // 库位盘点模式：无论数量是否与库存一致，均需填写备注
        currentEditItem.value = {
          index,
          item,
          formattedQuantity,
          actual,
          inventory,
          difference,
        };
        editRemarkRaw.value = item.remark || "";
        showRemarkModal.value = true;
      } catch (error) {
        console.error("保存盘点数据失败:", error);
        setStatus(`盘点失败：${error.message || "网络异常"}`, "error");
      } finally {
        isSubmitting.value = false;
      }
    };

    const saveInventoryData = async (
      index,
      item,
      formattedQuantity,
      remark,
    ) => {
      isSubmitting.value = true;
      try {
        const palletId = item.palletId ?? item.pallet_id ?? "";
        const response = await updateStockCheckDetail({
          inventeId: inventoryId,
          materialId: item.materialId,
          isBad: item.isBad || 0,
          actualNum: parseFloat(formattedQuantity),
          remark: remark.trim(),
          palletId,
        });

        if (response.success) {
          item.actualNum = formattedQuantity;
          item.remark = remark.trim();
          item.isInvente = 1;
          setStatus("盘点成功", "success");
          await fetchInventoryDetails(currentPage.value);
        } else {
          setStatus(`盘点失败：${response.msg || "更新失败"}`, "error");
        }
      } catch (error) {
        console.error("保存盘点数据失败:", error);
        setStatus(`盘点失败：${error.message || "网络异常"}`, "error");
      } finally {
        editingIndex.value = -1;
        isSubmitting.value = false;
        showRemarkModal.value = false;
        currentEditItem.value = null;
      }
    };

    const confirmRemark = async () => {
      if (currentEditItem.value) {
        const { index, item, formattedQuantity } = currentEditItem.value;
        await saveInventoryData(
          index,
          item,
          formattedQuantity,
          editRemarkRaw.value,
        );
      }
    };

    const cancelRemark = () => {
      showRemarkModal.value = false;
      currentEditItem.value = null;
      editRemarkRaw.value = "";
    };

    const cancelEdit = () => {
      editingIndex.value = -1;
      editingItemMeta.value = null;
      editRemarkRaw.value = "";
    };

    const handleEditKeydown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        applyEditQuantity();
      }
    };

    const handleConfirmClick = async (index, item) => {
      if (isSubmitting.value) return;
      try {
        await confirmEdit(index, item);
      } finally {
        setTimeout(() => {
          isSubmitting.value = false;
        }, 300);
      }
    };

    const handleBack = () => {
      router.back();
    };

    const fetchInventoryDetails = async (page = 1) => {
      try {
        loading.value = true;
        errorMsg.value = "";

        const params = {
          current: page,
          size: pageSize.value,
          inventeId: inventoryId,
          materialNo: searchQuery.value || "",
        };

        if (!activeFilter.value.includes(0)) {
          const qualityFilters = activeFilter.value.filter(
            (type) => type === 1 || type === 2,
          );
          if (qualityFilters.length > 0) {
            params.isBad = qualityFilters[0] === 1 ? 0 : 1;
          }

          const inventoryFilters = activeFilter.value.filter(
            (type) => type === 3 || type === 4,
          );
          if (inventoryFilters.length > 0) {
            params.isInvente = inventoryFilters[0] === 3 ? 1 : 0;
          }
        }

        const response = await getStockCheckDetailPage(params);

        if (response.success && response.data) {
          inventoryData.value = response.data.records || [];
          total.value = response.data.total || 0;
          currentPage.value = page;

          if (inventoryData.value.length > 0) {
            inventoryCheckStatus.value =
              inventoryData.value[0].checkStatus || 0;
          }
        } else {
          inventoryData.value = [];
          total.value = 0;
          errorMsg.value = response.msg || "获取盘点详情失败";
          inventoryCheckStatus.value = 0;
        }
      } catch (err) {
        errorMsg.value = "获取盘点详情失败，请稍后重试";
        console.error("获取盘点数据异常:", err);
        inventoryCheckStatus.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const showAuditPassConfirm = () => {
      showAuditPassConfirmModal.value = true;
    };

    const showAuditRejectConfirm = () => {
      showAuditRejectConfirmModal.value = true;
    };

    const handleAuditPass = async () => {
      try {
        isSubmitting.value = true;
        showAuditPassConfirmModal.value = false;

        const stockCheck = {
          id: inventoryId,
          checkStatus: 1,
        };

        const response = await updateStockCheckStatus(stockCheck);

        if (response.success) {
          ElMessage.success("审核通过成功");
          await fetchInventoryDetails();
        } else {
          ElMessage.error(response.msg || "审核操作失败");
        }
      } catch (error) {
        console.error("审核通过API调用失败:", error);
        ElMessage.error(error?.message || "审核操作失败");
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleAuditReject = async () => {
      if (!reason.value.trim()) {
        ElMessage.warning("请填写不通过原因");
        return;
      }

      try {
        isSubmitting.value = true;
        showAuditRejectConfirmModal.value = false;

        const stockCheck = {
          id: inventoryId,
          checkStatus: 2,
          reason: reason.value.trim(),
        };

        const response = await updateStockCheckStatus(stockCheck);

        if (response.success) {
          ElMessage.success("已标记为审核不通过");
          reason.value = "";
          await fetchInventoryDetails();
        } else {
          ElMessage.error(response.msg || "审核操作失败");
        }
      } catch (error) {
        console.error("审核不通过API调用失败:", error);
        ElMessage.error(error?.message || "审核操作失败");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      await fetchBizDept();
      await fetchInventoryDetails();
    });

    return {
      inventoryData,
      loading,
      errorMsg,
      showErrorModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      showAuditPassConfirmModal,
      showAuditRejectConfirmModal,
      activeFilter,
      editingIndex,
      editQuantityRaw,
      isSubmitting,
      currentPage,
      pageSize,
      total,
      searchQuery,
      showAddModal,
      showConfirmModal,
      suppliers,
      materials,
      selectedSupplier,
      selectedMaterial,
      supplierLoading,
      materialLoading,
      isAdding,
      bizDept,
      reason,
      inventoryCheckStatus,
      totalCount,
      inventoriedCount,
      inventoriedPercentage,
      handleBack,
      toggleFilter,
      startEdit,
      cancelEdit,
      handleEditKeydown,
      applyEditQuantity,
      handleConfirmClick,
      openCalculator,
      closeCalculator,
      showCalculatorModal,
      calcExpression,
      calcError,
      calcAppend,
      calcBackspace,
      calcClear,
      calcEvaluate,
      formatToTwoDecimals,
      calculateDifference,
      handlePageChange,
      handleSearch,
      fetchSuppliers,
      showRemarkModal,
      currentEditItem,
      editRemarkRaw,
      confirmRemark,
      cancelRemark,
      fetchMaterials,
      selectSupplier,
      selectMaterial,
      confirmAddMaterial,
      resetAddModal,
      handleSupplierSearch,
      handleMaterialSearch,
      showAuditPassConfirm,
      showAuditRejectConfirm,
      handleAuditPass,
      handleAuditReject,
      showLocationModal,
      locationLoading,
      locationList,
      locationMaterialNo,
      handleQueryLocation,
      showImageDialog,
      imageLoading,
      materialImageUrl,
      imageMaterialNo,
      imageLoadError,
      formatImageUrl,
      handleImageError,
      handleQueryImage,
      showImagePreview,
      previewImageUrl,
      previewScale,
      previewImageStyle,
      openImagePreview,
      closeImagePreview,
      zoomIn,
      zoomOut,
      resetZoom,
      toggleZoom,
      handleWheel,
      handleMouseDown,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    };
  },
};
</script>

<style scoped>
.manual-stock-check-detail {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--background-color, #f5f5f5);
  font-size: 15px;
  /* 与头部青绿主题统一，覆盖 Element 默认蓝 */
  --el-color-primary: var(--primary-color, #23b1c0);
  --el-color-primary-light-3: #4fc3cf;
  --el-color-primary-light-5: #7fd4dc;
  --el-color-primary-light-7: #afe4ea;
  --el-color-primary-light-8: #c7ecf0;
  --el-color-primary-light-9: #dff4f6;
  --el-color-primary-dark-2: #1c8e9a;
}

.manual-stock-check-detail :deep(.el-button) {
  font-size: 15px;
}

.manual-stock-check-detail :deep(.el-input__inner),
.manual-stock-check-detail :deep(.el-input__wrapper) {
  font-size: 15px;
}

.manual-stock-check-detail :deep(.el-tag) {
  font-size: 13px;
}

.manual-stock-check-detail :deep(.el-dialog__title),
.manual-stock-check-detail :deep(.el-form-item__label),
.manual-stock-check-detail :deep(.el-empty__description),
.manual-stock-check-detail :deep(.el-select .el-select__placeholder),
.manual-stock-check-detail :deep(.el-select .el-select__selected-item) {
  font-size: 15px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--header-background, #23b1c0);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 50px;
  box-sizing: border-box;
}

.back-btn {
  background: none;
  border: none;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-back {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
  color: var(--header-text, #fff);
}

.header :deep(.el-button--primary) {
  --el-button-bg-color: rgba(255, 255, 255, 0.2);
  --el-button-border-color: rgba(255, 255, 255, 0.65);
  --el-button-text-color: #fff;
  --el-button-hover-bg-color: rgba(255, 255, 255, 0.3);
  --el-button-hover-border-color: #fff;
  --el-button-hover-text-color: #fff;
  --el-button-active-bg-color: rgba(255, 255, 255, 0.35);
  --el-button-active-border-color: #fff;
}

.main-content {
  flex: 1;
  padding: 8px 10px;
  padding-top: 58px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.status-container {
  margin-bottom: 8px;
}

.search-container {
  margin-bottom: 8px;
}

.search-container :deep(.el-input__wrapper) {
  height: 42px;
}

.progress-container {
  padding: 12px;
  background: var(--card-background, #fff);
  border-radius: 8px;
  margin-bottom: 8px;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 15px;
  color: var(--text-secondary, #999);
}

.progress-count {
  font-weight: 500;
  color: var(--text-primary, #333);
}

.progress-container :deep(.el-progress-bar__inner) {
  background-color: var(--primary-color, #23b1c0);
}

.filter-nav {
  display: flex;
  justify-content: space-between;
  background: var(--card-background, #fff);
  border-radius: 8px;
  padding: 8px 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.filter-nav > .el-button {
  flex: 1;
}

.status-alert {
  margin-bottom: 8px;
}

.inventory-cards-wrapper {
  min-height: 200px;
}

.inventory-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inventory-card {
  background: var(--card-background, #fff);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-left: 3px solid var(--el-color-primary);
}

.card-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--el-border-color-lighter, #ebeef5);
}

.supplier-name {
  font-weight: 500;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.stock-info-inline {
  font-size: 15px;
  font-weight: 500;
  color: var(--warning-color);
  white-space: nowrap;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;
}

.material-code {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #333);
  text-align: left;
  width: 100%;
}

.material-desc {
  font-size: 15px;
  color: var(--text-secondary, #999);
  word-break: break-all;
  text-align: left;
  width: 100%;
}

.material-ware {
  font-size: 14px;
  color: var(--primary-color, #23b1c0);
  font-weight: 500;
  text-align: left;
  width: 100%;
  word-break: break-all;
}

.quantity-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.quantity-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.calc-btn {
  width: 100%;
}

.edit-actions {
  display: flex;
  gap: 8px;
}

.quantity-display {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  justify-content: space-between;
}

.qty-left {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
}

.qty-left .el-button {
  flex: 1;
  min-width: 0;
}

.actual-quantity {
  font-size: 16px;
  color: var(--text-primary, #333);
  font-weight: 500;
  white-space: nowrap;
}

.diff-remark-container {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter, #ebeef5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.qty-diff-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
}

.difference-section {
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
}

.difference-positive {
  color: var(--success-color, #67c23a);
  font-weight: 600;
}

.difference-negative {
  color: var(--error-color, #f56c6c);
  font-weight: 600;
}

.remark-display {
  font-size: 15px;
  flex: 1;
  min-width: 0;
}

.remark-label {
  font-weight: 500;
  color: var(--text-primary, #333);
}

.remark-text {
  color: var(--text-secondary, #999);
  word-break: break-all;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 10px 0 20px;
}

.audit-actions {
  display: flex;
  gap: 12px;
  padding: 12px 0;
}

.audit-actions > .el-button {
  flex: 1;
}

.calc-error {
  color: var(--error-color, #f56c6c);
  font-size: 14px;
  margin-top: 4px;
}

.calc-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.calc-key {
  height: 44px;
  border-radius: 8px;
}

.calc-key.op {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.3);
}

.calc-key.wide {
  grid-column: span 2;
}

.calc-key.primary {
  background: var(--primary-color, #23b1c0);
  border-color: var(--primary-color, #23b1c0);
  color: #fff;
  font-weight: 600;
}

.modal-info {
  padding: 12px;
  background: var(--background-color, #f5f5f5);
  border-radius: 8px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  color: var(--text-secondary, #999);
  font-weight: 500;
}

.info-value {
  color: var(--text-primary, #333);
  font-weight: 500;
}

.confirm-material-info {
  padding: 12px;
  border: 1px solid var(--el-border-color-lighter, #ebeef5);
  border-radius: 8px;
  background: rgba(35, 177, 192, 0.05);
}

.form-item {
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .header {
    padding: 8px 10px;
    height: 46px;
  }
  .title {
    font-size: 18px;
  }
  .main-content {
    padding: 6px 8px;
    padding-top: 54px;
  }
  .inventory-card {
    padding: 10px;
  }
}

/* ===== 物料图片弹窗 ===== */
.image-preview-area {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: var(--background-color, #f5f5f5);
}

.material-image {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  cursor: zoom-in;
  display: block;
}

.image-zoom-hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 12px;
  color: #fff;
  font-size: 13px;
}

.image-zoom-hint .el-icon {
  font-size: 14px;
}

/* ===== 图片大图预览 ===== */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  overflow: hidden;
  touch-action: none;
}

.image-preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
}

.image-preview-container:active {
  cursor: grabbing;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

.preview-toolbar {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.zoom-level {
  font-size: 15px;
  color: #fff;
  min-width: 48px;
  text-align: center;
  font-weight: 500;
}

.preview-hint {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .preview-toolbar {
    bottom: 32px;
    gap: 8px;
    padding: 6px 12px;
  }

  .preview-hint {
    bottom: 88px;
    font-size: 13px;
  }
}

/* 图片加载失败样式 */
.image-url-debug {
  margin-top: 10px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}
</style>
